import type { Option } from '@/components/ui/autocomplete';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from '@/components/ui/use-toast';
import { pharmacyClient } from '@/connect';
import { StockPointType } from '@/gen/auth/common_pb';
import { ItemType, RequestType, TransferRequestItem } from '@/gen/inventory/common_pb';
import { TransferInventoryRequestRequest } from '@/gen/inventory/transfer_inventory_request_pb';
import { getStockPoints, searchInventory } from '@/gen/pharmacy-PharmacyService_connectquery';
import { Loading } from '@/layouts/loading';
import { Route as branchTransferRoute } from '@/routes/company/_layout/inventory/transfer-stock/index';
import { useQuery } from '@connectrpc/connect-query';
import { useNavigate } from "@tanstack/react-router";
import { useDebounce } from '@uidotdev/usehooks';
import { Plus, Trash } from "lucide-react";
import { useEffect, useMemo, useState } from 'react';

interface ItemOption {
  value: string;
  label: string;
  stock: number;
  type: ItemType;
  requestType: "TRANSFER" | "PURCHASE_REQUEST";
}

const Index = () => {
  const navigate = useNavigate();
  const data = branchTransferRoute.useLoaderData();
  
  const [sourceBranch, setSourceBranch] = useState('');
  const [destinationBranch, setDestinationBranch] = useState('');
  const [sourceStockPoint, setSourceStockPoint] = useState('');
  const [destinationStockPoint, setDestinationStockPoint] = useState('');
  const [itemInput, setItemInput] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferItems, setTransferItems] = useState<{
    id: string;
    name: string;
    batchId: string;
    quantity: number;
    type: ItemType;
    requestType: "TRANSFER" | "PURCHASE_REQUEST";
    skuOptions: { value: string; label: string }[];
  }[]>([]);

  // Get stock points for source branch
  const { data: sourceStockPoints } = useQuery(getStockPoints, {
    startDate: Math.floor(Date.now() / 1000) - (30 * 24 * 60 * 60),
    endDate: Math.floor(Date.now() / 1000),
  }, {
    enabled: !!sourceBranch || !!destinationBranch,
  });

  const getStockPointLabel = (type: StockPointType): string => {
    return StockPointType[type].replace('STOCK_POINT_TYPE_', '');
  };

  // Auto-select destination receiving stock point when destination branch changes
  useEffect(() => {
    if (destinationBranch && sourceBranch && sourceBranch !== destinationBranch) {
      // Cross-branch: lock to receiving stock point
      const branch = data.branches.find(b => b.pharmacyCode === destinationBranch);
      if (branch) {
        setDestinationStockPoint(branch.stockPoints.find(sp => sp.stockPointType === StockPointType.RECEIVING)?.id || '');
      }
    } else if (destinationBranch && sourceBranch === destinationBranch) {
      // Same branch: reset destination stock point
      setDestinationStockPoint('');
    }
  }, [destinationBranch, sourceBranch, data.branches]);

  // Reset form when branches change
  useEffect(() => {
    if (sourceBranch) {
      setTransferItems([]);
      setItemInput('');
      setSourceStockPoint('');
    }
  }, [sourceBranch]);

  const searchDebounced = useDebounce(itemInput, 500);

  const { data: searchData, isLoading, error } = useQuery(searchInventory, {
    query: searchDebounced,
    limit: 10,
    onlyAvailable: true,
    stockPointId: sourceStockPoint,
  }, {
    enabled: !!sourceStockPoint && !!searchDebounced,
  });

  const itemsMap = useMemo(() => {
    if (!searchData?.items) return new Map<string, ItemOption>();
    
    const map = new Map<string, ItemOption>();
    searchData.items.forEach(item => {
      map.set(item.id, {
        value: item.id,
        label: `${item.tradeName} | ${item.unit} ${item.unitType}`,
        stock: item.availableStock,
        type: item.type,
        requestType: item.availableStock > 0 ? "TRANSFER" : "PURCHASE_REQUEST",
      });
    });
    return map;
  }, [searchData]);

  const searchOptions: Option[] = useMemo(() => {
    return Array.from(itemsMap.values()).map(item => ({
      value: item.value,
      label: item.label,
    }));
  }, [itemsMap]);

  const handleAddItem = async (option: ItemOption) => {
    if (!option) return;
    
    if (transferItems.some(item => item.id === option.value)) {
      setTransferItems(items => items.map(item => 
        item.id === option.value ? { ...item, quantity: item.quantity + 1 } : item
      ));
      setItemInput('');
      return;
    }
    
    let batchOptions: { value: string; label: string }[] = [];
    if (option.requestType === "TRANSFER") {
      try {
        const batches = await pharmacyClient.getInventory({
          page: 1,
          pageSize: 100,
          filter: {
            isAvailable: true,
            masterId: option.value,
            stockPointId: sourceStockPoint,
            itemType: option.type,
          }
        });
        
        batchOptions = batches.items.map(batch => ({
          value: batch.inventoryId,
          label: batch.expiryDate ? `${batch.batchNumber} - ${new Date(batch.expiryDate.toDate()).toLocaleDateString()}` : batch.batchNumber,
        }));
      } catch (err) {
        console.error('Error fetching batches:', err);
        toast({
          title: 'Error',
          description: 'Failed to fetch batch information',
          variant: 'destructive',
          duration: 3000,
        });
        return;
      }
    }
    
    setTransferItems(items => [
      ...items,
      {
        id: option.value,
        name: option.label,
        batchId: batchOptions.length > 0 ? batchOptions[0].value : '',
        quantity: 1,
        skuOptions: batchOptions,
        type: option.type,
        requestType: option.requestType,
      },
    ]);
    setItemInput('');
  };

  const handleBatchChange = (id: string, newBatchId: string) => {
    setTransferItems(items => items.map(item => item.id === id ? { ...item, batchId: newBatchId } : item));
  };

  const handleQuantityChange = (id: string, newQty: number) => {
    setTransferItems(items => items.map(item => 
      item.id === id ? { ...item, quantity: Math.max(1, newQty) } : item
    ));
  };

  const handleRemove = (id: string) => {
    setTransferItems(items => items.filter(item => item.id !== id));
  };

  const [loading, setLoading] = useState(false);

  const submitRequest = async () => {
    try {
      if (transferItems.length === 0) {
        toast({
          title: "Error",
          description: "Please add items to transfer before submitting.",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      if (!sourceStockPoint || !destinationStockPoint) {
        toast({
          title: "Error",
          description: "Please select both source and destination.",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      setLoading(true);
      const request = new TransferInventoryRequestRequest();
      request.destinationStockPointId = destinationStockPoint;
      request.sourceStockPointId = sourceStockPoint;
      request.transferNotes = transferNote;
      request.sourceBranchCode = sourceBranch;
      request.destinationBranchCode = destinationBranchName;
      request.sourceBranchName = sourceBranchName;
      request.destinationBranchName = destinationBranchName;

      const items: TransferRequestItem[] = [];
      for (const transferItem of transferItems) {
        const item = new TransferRequestItem();
        item.masterId = transferItem.id;
        item.batchId = transferItem.batchId;
        item.quantity = transferItem.quantity;
        item.requestType = transferItem.requestType === "TRANSFER" ? RequestType.TRANSFER : RequestType.PURCHASE;
        item.itemType = transferItem.type;
        item.notes = transferNote;
        items.push(item);
      }

      request.items = items;
      const response = await pharmacyClient.transferInventoryRequest(request);
      
      const isCrossBranch = sourceBranch !== destinationBranch;
      const successMessage = isCrossBranch 
        ? 'Cross-branch transfer request created successfully.'
        : response.purchaseRequestId !== "" && response.transferRequestId !== ""
          ? 'Transfer and Purchase Request created successfully.'
          : response.transferRequestId !== ""
            ? 'Transfer Request created successfully.'
            : 'Purchase Request created successfully.';
      
      toast({
        title: "Success",
        description: successMessage,
        variant: "success",
        duration: 5000,
      });
      
      setTransferItems([]);
      setSourceBranch('');
      setDestinationBranch('');
      setSourceStockPoint('');
      setItemInput('');
      setTransferNote('');
    } catch (error) {
      console.error("Error submitting transfer request:", error);
      toast({
        title: "Error",
        description: "Failed to submit transfer request. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const sourceBranchName = data.branches.find(b => b.pharmacyCode === sourceBranch)?.pharmacyName || 'Source Branch';
  const destinationBranchName = data.branches.find(b => b.pharmacyCode === destinationBranch)?.pharmacyName || 'Destination Branch';

  return (
    <div className="h-screen flex flex-col">
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate({ to: "/branch/inventory" })}
                style={{ cursor: "pointer" }}
              >
                Inventory Overview
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Transfer Stock</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <hr className='my-4' />
      
      <div className="flex flex-row gap-6" style={{ height: 'calc(100vh - 180px)' }}>
        {/* Left half */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 overflow-auto p-4 border rounded-lg">
            <h2 className="text-2xl font-semibold">Source &amp; Destination</h2>
            <p className="text-gray-600 mb-6">Select branches and stock points for inventory transfer.</p>
            
            <div className="space-y-4">
              {/* Source Branch */}
              <div>
                <p className="text-sm font-semibold mb-2">Source Branch</p>
                <Select value={sourceBranch} onValueChange={setSourceBranch}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Source Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {data.branches.map((branch) => (
                      <SelectItem key={branch.pharmacyCode} value={branch.pharmacyCode}>
                        {branch.pharmacyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Source Stock Point */}
              <div>
                <p className="text-sm font-semibold mb-2">Source Stock Point</p>
                <Select 
                  value={sourceStockPoint} 
                  onValueChange={setSourceStockPoint}
                  disabled={!sourceBranch}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Source Stock Point" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceStockPoints?.stockPoints
                      .filter(sp => sp.stockPointType !== StockPointType.UNKNOWN)
                      .map((sp) => (
                        <SelectItem key={sp.id} value={sp.id}>
                          {sp.name} ({getStockPointLabel(sp.stockPointType)})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Destination Branch */}
              <div>
                <p className="text-sm font-semibold mb-2">Destination Branch</p>
                <Select value={destinationBranch} onValueChange={setDestinationBranch}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Destination Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {data.branches.map((branch) => (
                      <SelectItem key={branch.pharmacyCode} value={branch.pharmacyCode}>
                        {branch.pharmacyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Destination Stock Point (Auto-selected, read-only) */}
              <div>
              <p className="text-sm font-semibold mb-2">
                Destination Stock Point {sourceBranch && destinationBranch && sourceBranch !== destinationBranch && "(Receiving - Auto-selected)"}
              </p>
              {sourceBranch && destinationBranch && sourceBranch !== destinationBranch ? (
                // Cross-branch: Show read-only receiving stock point
                <Input
                  value={
                    destinationBranch
                      ? data.branches.find(b => b.pharmacyCode === destinationBranch)?.stockPoints.find(sp => sp.stockPointType === StockPointType.RECEIVING)?.name || ''
                      : ''
                  }
                  disabled
                  placeholder="Auto-selected receiving point"
                  className="bg-gray-100"
                />
              ) : (
                // Same branch: Show selectable dropdown (excluding source stock point)
                <Select 
                  value={destinationStockPoint} 
                  onValueChange={setDestinationStockPoint}
                  disabled={!destinationBranch}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Destination Stock Point" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceStockPoints?.stockPoints
                      .filter(sp => sp.stockPointType !== StockPointType.UNKNOWN && sp.id !== sourceStockPoint)
                      .map((sp) => (
                        <SelectItem key={sp.id} value={sp.id}>
                          {sp.name} ({getStockPointLabel(sp.stockPointType)})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

              {/* Add Items */}
              <div>
                <p className="text-sm font-semibold mb-2">
                  Add Items from {sourceBranchName}
                </p>
                <input
                  type="text"
                  className="h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-2 w-full"
                  placeholder="Search for an item"
                  value={itemInput}
                  disabled={!sourceStockPoint || !destinationStockPoint}
                  onChange={(e) => setItemInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchOptions.length > 0) {
                      const firstOption = searchOptions[0];
                      const fullItem = itemsMap.get(firstOption.value);
                      if (fullItem) {
                        handleAddItem(fullItem);
                        setItemInput("");
                      }
                    }
                  }}
                />
                {error && (
                  <div className="text-red-500 text-sm mt-2">
                    Error loading items. Please try again.
                  </div>
                )}
                {itemInput && searchData?.items.length === 0 && (
                  <div className="text-gray-400 text-sm mt-2">No items found.</div>
                )}
                {(itemInput && isLoading) && (
                  <div className="text-gray-400 text-sm mt-2"><Loading /></div>
                )}
                {searchData?.items.length! > 0 && (
                  <div className="mt-2 border rounded-md bg-white shadow-sm overflow-auto divide-y">
                  {searchData?.items.slice(0, 5).map(opt => (
                    <div key={opt.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                      <div>
                        <div className="font-medium text-sm">{opt.tradeName} | {opt.unit} {opt.unitType}</div>
                        <div className="text-xs text-gray-500">Stock: {opt.availableStock} {opt.unitType}</div>
                      </div>
                      <button
                        type="button"
                        className="ml-4 p-2 rounded-full hover:bg-gray-100 text-primary"
                        onClick={() => handleAddItem({
                          value: opt.id,
                          label: opt.tradeName,
                          stock: opt.availableStock,
                          type: opt.type,
                          requestType: opt.availableStock > 0 ? "TRANSFER" : "PURCHASE_REQUEST",
                        })}
                        disabled={transferItems.some(item => item.id === opt.id)}
                        title={transferItems.some(item => item.id === opt.id) ? 'Already added' : 'Add'}
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right half */}
      <div className="flex-1 min-h-0 flex flex-col p-4 border rounded-lg">
        <h2 className="text-2xl font-semibold mb-2">Transfer Details</h2>
        <div className="flex flex-col flex-1 min-h-0">
          {transferItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-gray-400 gap-2">
              <span className="text-lg font-medium">No items selected</span>
              <span className="text-sm">
                {sourceStockPoint && destinationStockPoint
                  ? "Add items to transfer from the source stock point."
                  : "Please select both source and destination first."}
              </span>
            </div>
          ) : (
            <>
              <div className="w-full overflow-x-auto flex-1 min-h-0 border p-4 rounded-lg">
                <div className="grid grid-cols-12 gap-2 font-semibold text-sm border-b pb-2 mb-2">
                  <div className="col-span-5">Name</div>
                  <div className="col-span-3">Batch</div>
                  <div className="col-span-2">Quantity</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>
                {transferItems.map(item => (
                  <div key={item.id} className="grid grid-cols-12 gap-2 items-center border-b py-2 last:border-b-0">
                    <div className="col-span-5 truncate text-sm">{item.name}</div>
                    <div className="col-span-3">
                      {item.requestType === "TRANSFER" && (
                        <Select value={item.batchId} onValueChange={v => handleBatchChange(item.id, v)}>
                          <SelectTrigger className='h-8'>
                            <SelectValue placeholder="Select Batch" />
                          </SelectTrigger>
                          <SelectContent>
                            {item.skuOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {item.requestType === "PURCHASE_REQUEST" && (
                        <p className='text-sm text-gray-500'>
                          Purchase Request
                        </p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        className="w-16 text-xs px-2 py-1 h-8"
                        value={item.quantity}
                        onChange={e => handleQuantityChange(item.id, Math.max(0, Number(e.target.value)))}
                      />
                    </div>
                    <div className="col-span-2 text-right">
                      <Button
                        variant="ghost"
                        size="xs"
                        className="text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded"
                        onClick={() => handleRemove(item.id)}
                        title="Delete"
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-2 shrink-0 border-t pt-2">
                <Label htmlFor="transfer-notes" className="font-medium text-lg">Transfer Notes</Label>
                <Input
                  id="transfer-notes"
                  type="text"
                  className="text-xs"
                  placeholder="Add notes for this transfer"
                  value={transferNote}
                  onChange={e => setTransferNote(e.target.value)}
                />
                {sourceBranch && destinationBranch && sourceBranch !== destinationBranch && (
                  <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                    Cross-branch transfer: {sourceBranchName} → {destinationBranchName}
                  </div>
                )}
                <Button
                  className="mt-2 bg-primary text-white rounded-md px-4 py-2 w-full font-semibold hover:bg-primary/90 self-end"
                  onClick={submitRequest}
                  disabled={!sourceStockPoint || !destinationStockPoint || transferItems.length === 0 || loading }
                >
                  Submit Transfer Request
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);
};

export default Index;